// @flow
import React, {Component} from 'react';
import {compose} from 'redux';
import {connect} from 'react-redux';
import {FormattedMessage} from 'react-intl';
import classNames from 'classnames';
import FormattedCurrencyRange from '../FormattedCurrencyRange/FormattedCurrencyRange';
import {HA_SERP_MIN_MAX_PRICE_FIELDS} from '../../abacus/experiments';

import './PriceFilter.less';
import Slider from '../Slider/Slider';
import PriceInputs from '../PriceInputs/PriceInputs';
import {
    getLimitedMaxPrice,
    getLimitedMaxDisplayPrice,
    getUnlimitedMaxDisplayPrice
} from './utils';

import type {State as ReduxState} from '../../reducers/index';

export type BoundsType = Array<number | null>;
type ComponentProps = {
    currency: string,
    hasDates: ?boolean,
    minPrice: ?number,
    maxPrice: ?number,
    onPriceChange: (BoundsType) => void,
    className: string,
    locale: string
};

type State = {
    activeHandleIndex: ?number
};

type StateProps = {
    minMaxPriceFieldsTestBucket?: number
};

type Props = ComponentProps & StateProps;

export const nullIfMaxBounds = (
    maxAllowedPrice: ?number,
    callback: (BoundsType) => any
) => {
    return (bounds: BoundsType) => {
        let newBounds: BoundsType = [];
        if (maxAllowedPrice && bounds[1] && bounds[1] >= maxAllowedPrice) {
            newBounds = [bounds[0], null];
        } else {
            newBounds = newBounds.concat(bounds);
        }
        callback(newBounds);
    };
};

export class PriceFilter extends Component<Props, State> {
    constructor() {
        super();

        this.state = {
            activeHandleIndex: null
        };
    }

    handleSetActiveHandle = (index: ?number) => {
        this.setState({
            activeHandleIndex: index
        });
    };

    render() {
        let maxAllowedPrice;
        let maxDisplayPrice;
        let isMax;
        const props = this.props;
        const {
            currency,
            hasDates,
            onPriceChange,
            minMaxPriceFieldsTestBucket,
            className,
            locale
        } = props;
        const isMinMaxPriceFieldsTestV2 = minMaxPriceFieldsTestBucket === 2;
        const limitedMaxPrice = getLimitedMaxPrice(currency);
        const minPrice = props.minPrice || 0;

        if (isMinMaxPriceFieldsTestV2) {
            maxAllowedPrice = null;
            maxDisplayPrice = getUnlimitedMaxDisplayPrice(
                limitedMaxPrice,
                props.maxPrice
            );

            isMax = typeof props.maxPrice !== 'number';
        } else {
            maxAllowedPrice = limitedMaxPrice;

            maxDisplayPrice = getLimitedMaxDisplayPrice(
                maxAllowedPrice,
                props.maxPrice
            );
            isMax = maxDisplayPrice >= maxAllowedPrice;
        }

        const isAny: boolean = minPrice === 0 && isMax;

        const onChange = nullIfMaxBounds(maxAllowedPrice, onPriceChange);

        const getPriceFilterHeadlineLabel = () => {
            if (minMaxPriceFieldsTestBucket) {
                return (
                    <FormattedMessage
                        id={'pricePerNightLabel'}
                        defaultMessage={'Price per night'}
                    />
                );
            }

            if (isAny) {
                return (
                    <FormattedMessage
                        id={'priceAnyLabel'}
                        defaultMessage={'Any price'}
                    />
                );
            }

            return (
                <FormattedCurrencyRange
                    minPrice={minPrice}
                    maxPrice={maxDisplayPrice}
                    currency={currency}
                    isMax={isMax}
                    locale={locale}
                />
            );
        };

        const minMaxPriceFieldsTestClassName = minMaxPriceFieldsTestBucket
            ? `PriceFilter--min-max-price-fields-test-V${minMaxPriceFieldsTestBucket}`
            : '';

        const classes = classNames('PriceFilter', {
            [className]: className,
            [minMaxPriceFieldsTestClassName]: minMaxPriceFieldsTestBucket
        });

        return (
            <div
                role="menuitem"
                aria-labelledby={`filter-group-label-${className}`}
                className={classes}
            >
                <span
                    className="sr-only"
                    id={`filter-group-label-${className}`}
                >
                    <FormattedMessage
                        id={'filterBarPriceLabel'}
                        defaultMessage={'Price'}
                    />
                </span>
                <div className="PriceFilter__priceLabel">
                    <span className="PriceFilter__priceLabel__values">
                        {getPriceFilterHeadlineLabel()}
                    </span>
                    {!minMaxPriceFieldsTestBucket && (
                        <span className="PriceFilter__priceLabel__perNight">
                            {hasDates ? (
                                <FormattedMessage
                                    id={'ratesNightly'}
                                    defaultMessage={'per night'}
                                />
                            ) : (
                                <FormattedMessage
                                    id={'ratesAverage'}
                                    defaultMessage={'avg/night'}
                                />
                            )}
                        </span>
                    )}
                </div>
                {!!minMaxPriceFieldsTestBucket && (
                    <div
                        className="PriceFilter__PriceInputs"
                        data-wdio="PriceFilter__PriceInputs"
                    >
                        <PriceInputs
                            minPrice={minPrice}
                            maxPrice={maxDisplayPrice}
                            maxAllowedPrice={maxAllowedPrice}
                            isMax={isMax}
                            activeHandleIndex={this.state.activeHandleIndex}
                            onChange={onChange}
                            isMinMaxPriceFieldsTestV2
                        />
                    </div>
                )}
                {!isMinMaxPriceFieldsTestV2 && (
                    <div
                        className="PriceFilter__slider"
                        data-wdio="PriceFilter__slider"
                    >
                        <Slider
                            className={
                                minMaxPriceFieldsTestBucket
                                    ? 'Slider--min-max-price-fields-test'
                                    : ''
                            }
                            start={minPrice}
                            stop={maxDisplayPrice}
                            max={limitedMaxPrice}
                            activeHandleIndex={this.state.activeHandleIndex}
                            step={10}
                            onChange={onChange}
                            onSetActiveHandle={this.handleSetActiveHandle}
                            edapLabel="nightly_price_slider"
                        />
                    </div>
                )}
            </div>
        );
    }
}

const mapStateToProps = ({abacus}: ReduxState): StateProps => ({
    minMaxPriceFieldsTestBucket: (abacus[HA_SERP_MIN_MAX_PRICE_FIELDS] || {})
        .value
});

export default compose(connect(mapStateToProps))(PriceFilter);
